// src/hooks/useCameraControls.js
import { useState } from 'react';

export const useCameraControls = () => {
  const [stream, setStream] = useState(null);
  const [isStartingCamera, setIsStartingCamera] = useState(false);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [error, setError] = useState(null);
  const [currentFacingMode, setCurrentFacingMode] = useState('environment');

  // Función para detener cámara completamente
  const stopCameraCompletely = () => {
    return new Promise((resolve) => {
      try {
        if (stream) {
          console.log('🔄 Deteniendo stream completamente...');
          stream.getTracks().forEach(track => {
            try {
              track.stop();
              track.enabled = false;
            } catch (trackError) {
              console.warn('⚠️ Error deteniendo track:', trackError);
            }
          });
          setStream(null);
        }
        // Pausa más larga para asegurar que se detenga completamente
        setTimeout(() => {
          console.log('✅ Stream detenido completamente');
          resolve();
        }, 1000);
      } catch (error) {
        console.error('❌ Error deteniendo cámara:', error);
        setStream(null);
        resolve();
      }
    });
  };
  
  // Función para iniciar cámara
  const startCamera = async (mediaType, getVideoConstraints, facingMode) => {
    if (isStartingCamera || isSwitchingCamera) {
      console.log('🚫 Cámara ya iniciándose, ignorando...');
      return null;
    }

    try {
      setIsStartingCamera(true);
      setError(null);

      console.log('🎯 Iniciando cámara:', { mediaType, facingMode });

      let constraints = {};

      if (mediaType === 'audio') {
        constraints = {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 48000,
            channelCount: 2,
            autoGainControl: true
          },
          video: false
        };
      } else {
        constraints = {
          video: getVideoConstraints ? getVideoConstraints(facingMode) : {
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 },
            facingMode: facingMode || 'environment'
          },
          audio: mediaType === 'video' ? {
            echoCancellation: true,
            noiseSuppression: true,
            sampleRate: 48000,
            channelCount: 2,
            autoGainControl: true
          } : false
        };
      }

      console.log('📱 Constraints de cámara:', constraints);
      const newStream = await navigator.mediaDevices.getUserMedia(constraints);

      setStream(newStream);
      setError(null);
      
      // Actualizar el facingMode actual
      if (newStream && newStream.getVideoTracks().length > 0) {
        try {
          // Solo actualizar si no se está cambiando cámara (para evitar sobrescribir el facingMode deseado)
          if (!isSwitchingCamera) {
            const detectedFacingMode = newStream.getVideoTracks()[0].getSettings()?.facingMode || facingMode || 'environment';
            setCurrentFacingMode(detectedFacingMode);
            console.log('📱 FacingMode actual:', detectedFacingMode);
          } else {
            // Si se está cambiando cámara, usar el facingMode que se pasó
            setCurrentFacingMode(facingMode);
            console.log('📱 FacingMode cambiado a:', facingMode);
          }
        } catch (e) {
          console.warn('⚠️ No se pudo obtener facingMode:', e);
          // En caso de error, usar el facingMode que se pasó
          setCurrentFacingMode(facingMode);
        }
      }

      console.log('✅ Cámara iniciada exitosamente');
      return newStream;

    } catch (error) {
      console.error('❌ Error accediendo a medios:', error);
      let errorMessage = 'Error al acceder a la cámara';
      
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Permiso denegado para acceder a la cámara';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No se encontró ninguna cámara disponible';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'La cámara está siendo usada por otra aplicación';
      } else if (error.name === 'OverconstrainedError') {
        errorMessage = 'La cámara no cumple con los requisitos solicitados';
      }
      
      setError(errorMessage);
      setStream(null);
      setIsStartingCamera(false);
      setIsSwitchingCamera(false); // Limpiar también este estado
      throw error;
    } finally {
      setIsStartingCamera(false);
      setIsSwitchingCamera(false); // Asegurar que se limpie
    }
  };

  // Función para detener cámara
  const stopCamera = async () => {
    try {
      if (stream) {
        console.log('🛑 Deteniendo cámara...');
        stream.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
        setStream(null);
        // Pequeña pausa para asegurar que se detenga completamente
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    } catch (error) {
      console.error('❌ Error deteniendo cámara:', error);
    }
  };

  // Función para cambiar cámara (SOLO facingMode, NUNCA reiniciar)
  const switchCamera = async (mediaType, getVideoConstraints) => {
    if (isSwitchingCamera || isStartingCamera) {
      console.log('🚫 Ya cambiando cámara, ignorando...');
      return;
    }

    try {
      setIsSwitchingCamera(true);
      setError(null);

      // Determinar nueva dirección usando el estado actual
      const newFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';
      console.log('🔄 Cambiando facingMode de', currentFacingMode, 'a', newFacingMode);

      // SOLO cambiar facingMode del stream existente
      if (stream && stream.getVideoTracks().length > 0) {
        const videoTrack = stream.getVideoTracks()[0];
        
        try {
          await videoTrack.applyConstraints({
            facingMode: newFacingMode
          });
          
          setCurrentFacingMode(newFacingMode);
          console.log('✅ FacingMode cambiado exitosamente');
          
        } catch (constraintError) {
          console.warn('⚠️ No se pudo cambiar facingMode:', constraintError);
          // NO REINICIAR - Solo mostrar error y mantener cámara actual
          setError('No se pudo cambiar de cámara');
          console.log('⚠️ Manteniendo cámara actual');
        }
      } else {
        // Si no hay stream, NO hacer nada
        console.warn('⚠️ No hay stream activo para cambiar');
        setError('Cámara no disponible');
      }

    } catch (error) {
      console.error('❌ Error cambiando cámara:', error);
      setError('Error al cambiar de cámara');
    } finally {
      setIsSwitchingCamera(false);
    }
  };

  // Función para reiniciar cámara completamente
  const resetCamera = async () => {
    try {
      console.log('🔄 Reiniciando cámara completamente...');
      await stopCameraCompletely();
      setError(null);
      setIsStartingCamera(false);
      setIsSwitchingCamera(false);
      setCurrentFacingMode('environment'); // Resetear también el facingMode
      // Pausa adicional para asegurar reinicio completo
      await new Promise(resolve => setTimeout(resolve, 2000));
      console.log('✅ Cámara reiniciada completamente');
    } catch (error) {
      console.error('❌ Error reiniciando cámara:', error);
      setError(null);
      setIsStartingCamera(false);
      setIsSwitchingCamera(false);
      setCurrentFacingMode('environment');
    }
  };

  // Función para limpiar estados (emergencia)
  const clearStates = () => {
    console.log('🧹 Limpiando estados de cámara...');
    setIsStartingCamera(false);
    setIsSwitchingCamera(false);
    setError(null);
  };

  // Función para reiniciar y cambiar cámara
  const resetAndSwitchCamera = async (mediaType, getVideoConstraints) => {
    if (isSwitchingCamera || isStartingCamera) {
      console.log('🚫 Ya cambiando cámara, ignorando...');
      return;
    }

    try {
      setIsSwitchingCamera(true);
      setError(null);
      
      console.log('🔄 Reiniciando y cambiando cámara...');
      
      // Detener cámara actual
      await stopCameraCompletely();
      
      // Pausa corta
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Determinar nueva dirección usando el estado actual
      const newFacingMode = currentFacingMode === 'environment' ? 'user' : 'environment';
      
      console.log('🔄 Cambiando de', currentFacingMode, 'a', newFacingMode);
      
      // Iniciar nueva cámara
      await startCamera(mediaType, getVideoConstraints, newFacingMode);
      
    } catch (error) {
      console.error('❌ Error en resetAndSwitchCamera:', error);
      setError('Error al cambiar de cámara');
      // Limpiar estados en caso de error
      setIsStartingCamera(false);
    } finally {
      setIsSwitchingCamera(false);
    }
  };

  return {
    stream,
    isStartingCamera,
    isSwitchingCamera,
    error,
    currentFacingMode,
    startCamera,
    stopCamera,
    stopCameraCompletely,
    switchCamera,
    resetCamera,
    resetAndSwitchCamera,
    clearStates,
    setError
  };
};
